import React, { useState, useRef, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { withLocalize } from 'react-localize-redux';
import classNames from 'classnames';
import Icon, { IconSizes, IconVariants } from '../foundations/Icon';
import Tooltip from '../molecules/Tooltip';
import css from './EditableTitle.css';

const EditableTitle = ({
    containerStyle = {},
    value = '',
    placeholder = '',
    error = null,
    containerClass = null,
    onTextChanged = () => null,
    onTextChangeComplete = () => null,
    onFocus = () => null,
    selectOnFocus = false,
    maxLength = 40,
}) => {
    const [isEditSelected, setIsEditSelected] = useState(true);
    const eventNameInputRef = useRef(null);

    useEffect(() => {
        if (isEditSelected && eventNameInputRef.current) {
            eventNameInputRef.current.focus();
            if (selectOnFocus) {
                const input = eventNameInputRef.current;
                input.scrollLeft = 0;
                input.setSelectionRange(0, input.value.length);
            }
        }
    }, [isEditSelected, selectOnFocus]);

    const handleClick = useCallback(
        e => {
            e.stopPropagation();

            setIsEditSelected(true);

            if (!isEditSelected) {
                onFocus();
            }
        },
        [isEditSelected, onFocus]
    );

    const handleBlur = useCallback(() => {
        if (!isEditSelected) return;

        setIsEditSelected(false);
        onTextChangeComplete();
    }, [isEditSelected, onTextChangeComplete]);

    const handleTextChanged = useCallback(() => {
        onTextChanged(eventNameInputRef.current ? eventNameInputRef.current.value : '');
    }, [onTextChanged]);

    const handleKeyPress = useCallback(
        e => {
            if (e.key === 'Enter') handleBlur();
        },
        [handleBlur]
    );

    const content = (
        <div
            className={classNames(css.editableTitleHolder, containerClass, {
                [css.inputSelected]: isEditSelected,
                [css.error]: error,
                [css.empty]: !value?.trim(),
            })}
            onClick={handleClick}
            role="button"
            tabIndex={0}
            style={containerStyle}
        >
            <input
                type="text"
                className={css.editableInput}
                ref={eventNameInputRef}
                onChange={handleTextChanged}
                value={value}
                maxLength={maxLength}
                onBlur={handleBlur}
                placeholder={placeholder}
                onKeyPress={handleKeyPress}
            />
            <Icon
                name="edit-2-line"
                type="mingcute"
                size={IconSizes.SMALL}
                variant={isEditSelected ? IconVariants.DARK : IconVariants.LIGHT}
                className={css.pencil}
            />
            <span className={css.errorMessage}>{error}</span>
        </div>
    );

    if (!isEditSelected)
        return (
            <Tooltip position="top-start" title={value}>
                {content}
            </Tooltip>
        );

    return content;
};

EditableTitle.propTypes = {
    onTextChanged: PropTypes.func,
    onTextChangeComplete: PropTypes.func,
    onFocus: PropTypes.func,
    value: PropTypes.string,
    placeholder: PropTypes.string,
    containerStyle: PropTypes.objectOf(PropTypes.any),
    error: PropTypes.string,
    containerClass: PropTypes.string,
    selectOnFocus: PropTypes.bool,
    maxLength: PropTypes.number,
};

export default withLocalize(EditableTitle);
